import { useGesture } from '@use-gesture/react';
import { clsx } from 'clsx';
import { type ComponentPropsWithoutRef, forwardRef, useRef } from 'react';

import { useFocusRing } from '../../../core/hooks/useFocusRing.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import * as styles from './ResizeSeparator.sty.js';

/**
 * @internal
 */
export interface ResizeSeparatorProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'onDrag' | 'onDragEnd' | 'onDragStart'
> {
  /**
   * Id of the element whose size the separator controls.
   */
  controls: string;
  /**
   * Current size of the controlled element, in pixels.
   */
  value: number;
  /**
   * Smallest size the controlled element can take, in pixels.
   */
  min: number;
  /**
   * Largest size the controlled element can take, in pixels. `Infinity` before
   * the container is measured.
   */
  max: number;
  /**
   * Human readable form of `value`.
   */
  valueText: string;
  /**
   * Called when the user starts dragging the separator.
   */
  onDragStart?: () => void;
  /**
   * Called while dragging, with the horizontal movement in pixels.
   */
  onDrag?: (movement: number) => void;
  /**
   * Called when the user stops dragging the separator.
   */
  onDragEnd?: () => void;
}

/**
 * Draggable and keyboard focusable separator between two resizable areas.
 *
 * Follows the Window Splitter ARIA pattern.
 * https://www.w3.org/WAI/ARIA/apg/patterns/windowsplitter/
 *
 * @internal
 */
export const ResizeSeparator = forwardRef<HTMLDivElement, ResizeSeparatorProps>(
  (props, forwardedRef) => {
    const {
      controls,
      value,
      min,
      max,
      valueText,
      onDrag,
      onDragStart,
      onDragEnd,
      className,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...rest
    } = props;

    const ref = useRef<HTMLDivElement>(null);
    const mergedRefs = useMergeRefs([ref, forwardedRef]);

    const { focusProps, focusClassName } = useFocusRing({
      focusWithin: true,
      ignoreModality: true,
    });

    useGesture(
      {
        // Wrapped rather than passed through: the gesture engine dispatches on
        // key presence, not on the value, so handing it an absent optional
        // callback throws mid-gesture and kills the drag.
        onDragStart: () => {
          onDragStart?.();
        },
        onDragEnd: () => {
          onDragEnd?.();
        },
        onDrag: (state) => {
          onDrag?.(state.movement[0]);
        },
      },
      { target: ref },
    );

    return (
      <div
        {...rest}
        {...focusProps}
        role="separator"
        ref={mergedRefs}
        className={clsx(styles.separator, focusClassName, className)}
        tabIndex={0}
        aria-controls={controls}
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-orientation="vertical"
        aria-valuemin={min}
        // An omitted `aria-valuemax` implies 100, which any pixel value above
        // that would sit outside of. An unbounded panel therefore reports its
        // current width as the maximum instead of dropping the attribute.
        aria-valuemax={isFinite(max) ? max : Math.max(min, value)}
        aria-valuenow={value}
        aria-valuetext={valueText}
      />
    );
  },
);
